import { createStore, reconcile } from "solid-js/store"
import { createEffect, createMemo } from "solid-js"
import { createSimpleContext } from "@openanalyst-ai/ui/context"
import { persisted } from "@/utils/persist"
import {
  controlRoomDefaults,
  type ControlRoomFlag,
  type ControlRoomSettings,
} from "@/control-room/settings"
import { migrateSettingsState } from "./settings-migration"

export { migrateSettingsState } from "./settings-migration"

export interface NotificationSettings {
  agent: boolean
  permissions: boolean
  errors: boolean
}

export interface SoundSettings {
  agentEnabled: boolean
  agent: string
  permissionsEnabled: boolean
  permissions: string
  errorsEnabled: boolean
  errors: string
}

export interface Settings {
  general: {
    autoSave: boolean
    releaseNotes: boolean
    followup: "queue" | "steer"
    showFileTree: boolean
    showNavigation: boolean
    showSearch: boolean
    showStatus: boolean
    showTerminal: boolean
    showReasoningSummaries: boolean
    shellToolPartsExpanded: boolean
    editToolPartsExpanded: boolean
    showSessionProgressBar: boolean
    newLayoutDesigns: boolean
    showCustomAgents: boolean
    pluginChromeV2: boolean
    pluginCanvas: boolean
    onboardingV2: boolean
    starterProjectRoot: string
    devicePermissions: {
      camera: boolean
      microphone: boolean
      files: boolean
    }
  }
  updates: {
    startup: boolean
  }
  appearance: {
    fontSize: number
    mono: string
    sans: string
    terminal: string
  }
  keybinds: Record<string, string>
  permissions: {
    autoApprove: boolean
  }
  notifications: NotificationSettings
  sounds: SoundSettings
  controlRoom: ControlRoomSettings
  migrations: {
    desktopMiniChatDefault: boolean
    followupQueueDefault: boolean
  }
}

export const monoDefault = "System Mono"
export const sansDefault = "System Sans"
export const terminalDefault = "JetBrainsMono Nerd Font Mono"

const monoFallback =
  'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'
const sansFallback = 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
const terminalFallback =
  '"JetBrainsMono Nerd Font Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace'

const monoBase = monoFallback
const sansBase = sansFallback
const terminalBase = terminalFallback

function input(font: string | undefined) {
  return font ?? ""
}

function family(font: string) {
  if (/^[\w-]+$/.test(font)) return font
  return `"${font.replaceAll("\\", "\\\\").replaceAll('"', '\\"')}"`
}

function stack(font: string | undefined, base: string) {
  const value = font?.trim() ?? ""
  if (!value) return base
  return `${family(value)}, ${base}`
}

export function monoInput(font: string | undefined) {
  return input(font)
}

export function sansInput(font: string | undefined) {
  return input(font)
}

export function monoFontFamily(font: string | undefined) {
  return stack(font, monoBase)
}

export function sansFontFamily(font: string | undefined) {
  return stack(font, sansBase)
}

export function terminalInput(font: string | undefined) {
  return input(font)
}

export function terminalFontFamily(font: string | undefined) {
  return stack(font, terminalBase)
}

const defaultSettings: Settings = {
  general: {
    autoSave: true,
    releaseNotes: true,
    followup: "queue",
    showFileTree: true,
    showNavigation: false,
    showSearch: false,
    showStatus: false,
    showTerminal: false,
    showReasoningSummaries: false,
    shellToolPartsExpanded: false,
    editToolPartsExpanded: false,
    showSessionProgressBar: true,
    newLayoutDesigns: false,
    showCustomAgents: true,
    pluginChromeV2: false, // Plugin chrome v2 — default off in main; opt in via the settings store.
    pluginCanvas: true, // TEMP verify-live: confirm canvas shell renders. Revert to false before commit.
    onboardingV2: true, // Onboarding v2 — default ON for production (new + returning users); opt out via OPENANALYST_DEV_DISABLE_ONBOARDING_V2 / settings store.
    starterProjectRoot: "",
    // Device access — default ON for everything; the desktop main process
    // mirrors camera/microphone into its Chromium permission handler.
    devicePermissions: {
      camera: true,
      microphone: true,
      files: true,
    },
  },
  updates: {
    startup: true,
  },
  appearance: {
    fontSize: 14,
    mono: "",
    sans: "",
    terminal: "",
  },
  keybinds: {},
  permissions: {
    autoApprove: false,
  },
  notifications: {
    agent: true,
    permissions: true,
    errors: false,
  },
  sounds: {
    agentEnabled: true,
    agent: "staplebops-01",
    permissionsEnabled: true,
    permissions: "staplebops-02",
    errorsEnabled: true,
    errors: "nope-03",
  },
  controlRoom: controlRoomDefaults({
    channel: import.meta.env.VITE_OPENANALYST_CHANNEL,
    platform: "desktop",
  }),
  migrations: {
    desktopMiniChatDefault: true,
    followupQueueDefault: true,
  },
}

function withFallback<T>(read: () => T | undefined, fallback: T) {
  return createMemo(() => read() ?? fallback)
}

export const { use: useSettings, provider: SettingsProvider } = createSimpleContext({
  name: "Settings",
  init: () => {
    const [store, setStore, _, ready] = persisted(
      { key: "settings.v3", migrate: migrateSettingsState },
      createStore<Settings>(defaultSettings),
    )

    createEffect(() => {
      if (typeof document === "undefined") return
      const root = document.documentElement
      root.style.setProperty("--font-family-mono", monoFontFamily(store.appearance?.mono))
      root.style.setProperty("--font-family-sans", sansFontFamily(store.appearance?.sans))
    })

    return {
      ready,
      get current() {
        return store
      },
      general: {
        autoSave: withFallback(() => store.general?.autoSave, defaultSettings.general.autoSave),
        setAutoSave(value: boolean) {
          setStore("general", "autoSave", value)
        },
        devicePermissions: {
          camera: withFallback(
            () => store.general?.devicePermissions?.camera,
            defaultSettings.general.devicePermissions.camera,
          ),
          microphone: withFallback(
            () => store.general?.devicePermissions?.microphone,
            defaultSettings.general.devicePermissions.microphone,
          ),
          files: withFallback(
            () => store.general?.devicePermissions?.files,
            defaultSettings.general.devicePermissions.files,
          ),
        },
        setDevicePermission(device: "camera" | "microphone" | "files", value: boolean) {
          setStore("general", "devicePermissions", {
            ...defaultSettings.general.devicePermissions,
            ...(store.general?.devicePermissions ?? {}),
            [device]: value,
          })
        },
        releaseNotes: withFallback(() => store.general?.releaseNotes, defaultSettings.general.releaseNotes),
        setReleaseNotes(value: boolean) {
          setStore("general", "releaseNotes", value)
        },
        followup: withFallback(() => store.general?.followup, defaultSettings.general.followup),
        setFollowup(value: "queue" | "steer") {
          setStore("general", "followup", value)
        },
        showFileTree: withFallback(() => store.general?.showFileTree, defaultSettings.general.showFileTree),
        setShowFileTree(value: boolean) {
          setStore("general", "showFileTree", value)
        },
        showNavigation: withFallback(() => store.general?.showNavigation, defaultSettings.general.showNavigation),
        setShowNavigation(value: boolean) {
          setStore("general", "showNavigation", value)
        },
        showSearch: withFallback(() => store.general?.showSearch, defaultSettings.general.showSearch),
        setShowSearch(value: boolean) {
          setStore("general", "showSearch", value)
        },
        showStatus: withFallback(() => store.general?.showStatus, defaultSettings.general.showStatus),
        setShowStatus(value: boolean) {
          setStore("general", "showStatus", value)
        },
        showTerminal: withFallback(() => store.general?.showTerminal, defaultSettings.general.showTerminal),
        setShowTerminal(value: boolean) {
          setStore("general", "showTerminal", value)
        },
        showReasoningSummaries: withFallback(
          () => store.general?.showReasoningSummaries,
          defaultSettings.general.showReasoningSummaries,
        ),
        setShowReasoningSummaries(value: boolean) {
          setStore("general", "showReasoningSummaries", value)
        },
        shellToolPartsExpanded: withFallback(
          () => store.general?.shellToolPartsExpanded,
          defaultSettings.general.shellToolPartsExpanded,
        ),
        setShellToolPartsExpanded(value: boolean) {
          setStore("general", "shellToolPartsExpanded", value)
        },
        editToolPartsExpanded: withFallback(
          () => store.general?.editToolPartsExpanded,
          defaultSettings.general.editToolPartsExpanded,
        ),
        setEditToolPartsExpanded(value: boolean) {
          setStore("general", "editToolPartsExpanded", value)
        },
        showSessionProgressBar: withFallback(
          () => store.general?.showSessionProgressBar,
          defaultSettings.general.showSessionProgressBar,
        ),
        setShowSessionProgressBar(value: boolean) {
          setStore("general", "showSessionProgressBar", value)
        },
        newLayoutDesigns: withFallback(
          () => store.general?.newLayoutDesigns,
          defaultSettings.general.newLayoutDesigns,
        ),
        setNewLayoutDesigns(value: boolean) {
          setStore("general", "newLayoutDesigns", value)
        },
        showCustomAgents: withFallback(
          () => store.general?.showCustomAgents,
          defaultSettings.general.showCustomAgents,
        ),
        setShowCustomAgents(value: boolean) {
          setStore("general", "showCustomAgents", value)
        },
        pluginChromeV2: withFallback(
          () => store.general?.pluginChromeV2,
          defaultSettings.general.pluginChromeV2,
        ),
        setPluginChromeV2(value: boolean) {
          setStore("general", "pluginChromeV2", value)
        },
        pluginCanvas: withFallback(
          () => store.general?.pluginCanvas,
          defaultSettings.general.pluginCanvas,
        ),
        setPluginCanvas(value: boolean) {
          setStore("general", "pluginCanvas", value)
        },
        onboardingV2: withFallback(
          () => store.general?.onboardingV2,
          defaultSettings.general.onboardingV2,
        ),
        setOnboardingV2(value: boolean) {
          setStore("general", "onboardingV2", value)
        },
        starterProjectRoot: withFallback(
          () => store.general?.starterProjectRoot,
          defaultSettings.general.starterProjectRoot,
        ),
        setStarterProjectRoot(value: string) {
          setStore("general", "starterProjectRoot", value)
        },
      },
      updates: {
        startup: withFallback(() => store.updates?.startup, defaultSettings.updates.startup),
        setStartup(value: boolean) {
          setStore("updates", "startup", value)
        },
      },
      appearance: {
        fontSize: withFallback(() => store.appearance?.fontSize, defaultSettings.appearance.fontSize),
        setFontSize(value: number) {
          setStore("appearance", "fontSize", value)
        },
        font: withFallback(() => store.appearance?.mono, defaultSettings.appearance.mono),
        setFont(value: string) {
          setStore("appearance", "mono", value.trim() ? value : "")
        },
        uiFont: withFallback(() => store.appearance?.sans, defaultSettings.appearance.sans),
        setUIFont(value: string) {
          setStore("appearance", "sans", value.trim() ? value : "")
        },
        terminalFont: withFallback(() => store.appearance?.terminal, defaultSettings.appearance.terminal),
        setTerminalFont(value: string) {
          setStore("appearance", "terminal", value.trim() ? value : "")
        },
      },
      keybinds: {
        get: (action: string) => store.keybinds?.[action],
        set(action: string, keybind: string) {
          setStore("keybinds", action, keybind)
        },
        reset(action: string) {
          setStore("keybinds", (current) => {
            if (!Object.prototype.hasOwnProperty.call(current, action)) return current
            const next = { ...current }
            delete next[action]
            return next
          })
        },
        resetAll() {
          setStore("keybinds", reconcile({}))
        },
      },
      permissions: {
        autoApprove: withFallback(() => store.permissions?.autoApprove, defaultSettings.permissions.autoApprove),
        setAutoApprove(value: boolean) {
          setStore("permissions", "autoApprove", value)
        },
      },
      notifications: {
        agent: withFallback(() => store.notifications?.agent, defaultSettings.notifications.agent),
        setAgent(value: boolean) {
          setStore("notifications", "agent", value)
        },
        permissions: withFallback(() => store.notifications?.permissions, defaultSettings.notifications.permissions),
        setPermissions(value: boolean) {
          setStore("notifications", "permissions", value)
        },
        errors: withFallback(() => store.notifications?.errors, defaultSettings.notifications.errors),
        setErrors(value: boolean) {
          setStore("notifications", "errors", value)
        },
      },
      sounds: {
        agentEnabled: withFallback(() => store.sounds?.agentEnabled, defaultSettings.sounds.agentEnabled),
        setAgentEnabled(value: boolean) {
          setStore("sounds", "agentEnabled", value)
        },
        agent: withFallback(() => store.sounds?.agent, defaultSettings.sounds.agent),
        setAgent(value: string) {
          setStore("sounds", "agent", value)
        },
        permissionsEnabled: withFallback(
          () => store.sounds?.permissionsEnabled,
          defaultSettings.sounds.permissionsEnabled,
        ),
        setPermissionsEnabled(value: boolean) {
          setStore("sounds", "permissionsEnabled", value)
        },
        permissions: withFallback(() => store.sounds?.permissions, defaultSettings.sounds.permissions),
        setPermissions(value: string) {
          setStore("sounds", "permissions", value)
        },
        errorsEnabled: withFallback(() => store.sounds?.errorsEnabled, defaultSettings.sounds.errorsEnabled),
        setErrorsEnabled(value: boolean) {
          setStore("sounds", "errorsEnabled", value)
        },
        errors: withFallback(() => store.sounds?.errors, defaultSettings.sounds.errors),
        setErrors(value: string) {
          setStore("sounds", "errors", value)
        },
      },
      controlRoom: {
        set(flag: ControlRoomFlag, value: boolean) {
          setStore("controlRoom", flag, value)
        },
        previewPanel: withFallback(() => store.controlRoom?.previewPanel, defaultSettings.controlRoom.previewPanel),
        sessionSwitcher: withFallback(
          () => store.controlRoom?.sessionSwitcher,
          defaultSettings.controlRoom.sessionSwitcher,
        ),
        readOnlySubagentPanel: withFallback(
          () => store.controlRoom?.readOnlySubagentPanel,
          defaultSettings.controlRoom.readOnlySubagentPanel,
        ),
        multiRunFusion: withFallback(
          () => store.controlRoom?.multiRunFusion,
          defaultSettings.controlRoom.multiRunFusion,
        ),
        miniChat: withFallback(() => store.controlRoom?.miniChat, defaultSettings.controlRoom.miniChat),
        settingsPolish: withFallback(
          () => store.controlRoom?.settingsPolish,
          defaultSettings.controlRoom.settingsPolish,
        ),
      },
    }
  },
})
